import { Match, MatchRenderProps } from '@reach/router';
import { parse } from 'query-string';
import React from 'react';
import { MapStateToProps, connect } from 'react-redux';
import { IProjectStore } from 'store/companies/companiesTypes';
import { IProviders } from 'store/providers/providerTypes';
import { ICompanies, RootState } from 'store/store';

import messages, { projectRoutesName } from './Messages';

import APopover from '../APopover';
import ActionLink from '../ActionLink/ActionLink';
import Icon from '../Icon/Icon';
import { RouteName } from './styles';

interface IStateToProps {
  providers: IProviders['map'];
  companies: ICompanies['data'];
  projects: ICompanies['data'][0]['projects'];
}

type IComponentProps = IStateToProps;

interface IMatchProps {
  roleId?: string;
  providerId?: string;
}

interface IMatchProjectMembersProps {
  projectId?: string;
}

const RouteMatch: React.FC<IComponentProps> = (props) => (
  <RouteName>
    <Match path="/homepage">
      {(matchProps) => matchProps.match && <span>{messages.home}</span>}
    </Match>
    <Match path="/content/:providerId">
      {(matchProps: MatchRenderProps<IMatchProps>) => {
        let providerName = '';
        if (matchProps.match) {
          providerName =
            props.providers[Number(matchProps.match.providerId)]?.data?.name ||
            '';
        }
        return (
          matchProps.match && <span>{messages.content(providerName)}</span>
        );
      }}
    </Match>
    <Match path="/content/:providerId/settings">
      {(matchProps) =>
        matchProps.match && <span>{messages.contentSettings}</span>
      }
    </Match>
    <Match path="/content/:providerId/tags">
      {(matchProps) => matchProps.match && <span>{messages.tags}</span>}
    </Match>
    <Match path="/content/:providerId/templates">
      {(matchProps) =>
        matchProps.match && <span>{messages.providerTemplates}</span>
      }
    </Match>
    <Match path="/content/:providerId/search">
      {(matchProps) => {
        return (
          matchProps.match &&
          matchProps.location.search && (
            <span>
              {messages.searchFor(parse(matchProps.location.search).q)}
            </span>
          )
        );
      }}
    </Match>
    <Match path="/content/:providerId/access/:roleId">
      {(matchProps: MatchRenderProps<IMatchProps>) => {
        let companyId;
        let roleName;
        if (matchProps.match) {
          companyId =
            props.providers[Number(matchProps.match.providerId)].data!
              .managingCompanyId;
          roleName =
            companyId &&
            props.companies[companyId].roles[Number(matchProps.match.roleId)]
              .name;
        }
        return matchProps.match && roleName && <span>{roleName}</span>;
      }}
    </Match>
    <Match path="/c/company/list">
      {(matchProps) =>
        matchProps.match && <span>{messages.companiesManagement}</span>
      }
    </Match>
    <Match path="/c/profile">
      {(matchProps) => matchProps.match && <span>{messages.profile}</span>}
    </Match>
    <Match path="project/editor">
      {(matchProps) =>
        matchProps.match && <span>{projectRoutesName('create')}</span>
      }
    </Match>
    <Match path="project/editor/:projectId">
      {(matchProps: MatchRenderProps<IMatchProjectMembersProps>) => {
        let projectName;
        if (matchProps.match) {
          projectName = props.projects[Number(matchProps.match.projectId)].name;
        }
        return (
          matchProps.match && (
            <span>{projectRoutesName('editor', projectName)}</span>
          )
        );
      }}
    </Match>
    <Match path="project/:projectId/members">
      {(matchProps: MatchRenderProps<IMatchProjectMembersProps>) => {
        let projectName;
        if (matchProps.match) {
          projectName = props.projects[Number(matchProps.match.projectId)].name;
        }
        return (
          matchProps.match && (
            <span>{projectRoutesName('members', projectName)}</span>
          )
        );
      }}
    </Match>
    <Match path="project/:projectId/templates">
      {(matchProps: MatchRenderProps<IMatchProjectMembersProps>) => {
        let projectName;
        if (matchProps.match) {
          projectName = props.projects[Number(matchProps.match.projectId)].name;
        }
        return (
          matchProps.match && (
            <span>{projectRoutesName('templates', projectName)}</span>
          )
        );
      }}
    </Match>
    <Match path="project/:projectId/task/*">
      {(matchProps: MatchRenderProps<IMatchProjectMembersProps>) => {
        let projectName;
        let description;
        if (matchProps.match) {
          projectName =
            props.projects[Number(matchProps.match.projectId)]?.name ?? '';
          description =
            props.projects[Number(matchProps.match.projectId)]?.description ??
            '';
        }
        return (
          matchProps.match && (
            <span>
              <span>{projectRoutesName('task', projectName)}</span>
              <span>
                {description && (
                  <APopover
                    arrow={true}
                    curveBorder={true}
                    trigger="click"
                    target={
                      <ActionLink color={'blue'}>
                        <Icon type="info" style={{ padding: '0rem 0.3rem' }} />
                      </ActionLink>
                    }
                  >
                    <div
                      style={{
                        maxWidth: '500px',
                        whiteSpace: 'pre-wrap',

                        maxHeight: '90vh',
                        overflow: 'scroll',
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: description }} />
                      {/*<CKEditorField
                        editor={ClassicEditor}
                        data={description}
                        disabled={true}
                        dir="rtl"
                        config={{
                          link: {
                            // Automatically add target="_blank" and rel="noopener noreferrer" to all external links.
                            addTargetToExternalLinks: true,
                          },
                          toolbar: [],
                          language: 'fa',
                        }}
                      />*/}
                    </div>
                  </APopover>
                )}
              </span>
            </span>
          )
        );
      }}
    </Match>
    <Match path="/not-found">
      {(matchProps) => matchProps.match && <span>{messages.notFound}</span>}
    </Match>
  </RouteName>
);

const mapStateToProps: MapStateToProps<IStateToProps, undefined, RootState> = (
  state
) => {
  const providers = state.providers.map;
  const companies = state.companies.data;
  const projects: Array<IProjectStore> = [];
  Object.values(companies).forEach((comp) =>
    Object.values(comp.projects || {}).forEach(
      (prj) => (projects[prj.id] = prj)
    )
  );

  return { providers, companies, projects };
};

export default connect(mapStateToProps)(RouteMatch);
